import React from "react";
import { DateTime } from "luxon";
import { Identifiable, Resource, UnavailabilityReason } from "@planzone/dto";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ResourceAvatar } from "@common/resourceAvatar";
import { usePlanzonePermissions } from "@components/permissions";
import { TextInput } from "@common/textInput";
import { DateTimeInput } from "@common/dateTimeInput";
import { SelectList } from "@common/selectList";
import { PagedResources } from "@common/pagedResources";
import { ArrowIcon } from "@common/assets/arrowIcon";
import { Props } from "./types";
import styles from "./styles.scss";
import { Checkbox } from "@common/checkbox";
import { TimeInput } from "@common/timeInput";
import { calculateHours } from "@scenes/calendar/week/dayCreationMenu/utils";

function UnavailabilityEditionForm({
  formData,
  editForm,
  reasons,
  accountResource,
  error,
}: Props) {
  const { t } = useTranslation();
  const { isAdministrator } = usePlanzonePermissions();
  const userTimezone = DateTime.local().zoneName;
  const editType = (reason: Identifiable<UnavailabilityReason>) =>
    editForm({ ...formData, reason });
  const editResource = (resource: Identifiable<Resource>) =>
    editForm({ ...formData, resource });
  const editMode = (partialUnavailability: boolean) =>
    formData.partialUnavailability !== partialUnavailability &&
    editForm({
      ...formData,
      partialUnavailability,
      startDate: "",
      endDate: "",
      nonWorkedHoursCount: 0,
    });

  return (
    <div className={styles.unavailabilityEditionForm}>
      <div className={styles.row}>
        <div className={styles.group}>
          <h3 className={styles.title}>{t("unavailability.creation.name")}</h3>
          <TextInput
            value={formData.label}
            placeholder={t("unavailability.creation.namePlaceholder")}
            input={(label) => editForm({ ...formData, label })}
            isInvalid={error.emptyName}
          />
        </div>
        {isAdministrator && (
          <div className={styles.group}>
            <h3 className={styles.title}>{t("unavailability.creation.for")}</h3>
            <SelectList<Identifiable<Resource>>
              listClassName={styles.listSelect}
            >
              {({ isOpen, select }) => ({
                input: (
                  <div
                    className={classNames(styles.selectInput, {
                      [styles.invalidSelectInput]:
                        formData.resource.uuid === "",
                      [styles.inputOpen]: isOpen,
                    })}
                  >
                    <div className={styles.avatar}>
                      <ResourceAvatar resource={formData.resource} />
                    </div>
                    <p>
                      {formData.resource.uuid === ""
                        ? t("unavailability.creation.forPlaceholder")
                        : `${formData.resource.name}${
                            formData.resource.uuid === accountResource.uuid
                              ? ` (${t("unavailability.creation.me")})`
                              : ""
                          }`}
                    </p>
                    <ArrowIcon
                      className={classNames(styles.arrowIcon, {
                        [styles.arrowIconOpen]: isOpen,
                        [styles.arrowIconClose]: !isOpen,
                      })}
                    />
                  </div>
                ),
                list: (
                  <PagedResources
                    flagName="unavailabilityResourcesFlag"
                    className={styles.pagedList}
                  >
                    {(resources) =>
                      resources.map((resource) => (
                        <div
                          key={resource.uuid}
                          className={classNames(styles.listItem, {
                            [styles.selectedItem]:
                              resource.uuid === formData.resource.uuid,
                          })}
                          onClick={() => editResource(select(resource))}
                        >
                          <div className={styles.avatar}>
                            <ResourceAvatar resource={resource} />
                          </div>
                          <p className={styles.itemName}>{`${resource.name}${
                            resource.uuid === accountResource.uuid
                              ? ` (${t("unavailability.creation.me")})`
                              : ""
                          }`}</p>
                        </div>
                      ))
                    }
                  </PagedResources>
                ),
              })}
            </SelectList>
          </div>
        )}
      </div>
      <div className={classNames(styles.row, styles.reasonRow)}>
        <div className={styles.group}>
          <h3 className={styles.title}>
            {t("unavailability.creation.reason")}
          </h3>
          <SelectList<Identifiable<UnavailabilityReason>>
            listClassName={styles.listSelect}
          >
            {({ isOpen, select }) => ({
              input: (
                <div
                  className={classNames(styles.reasonSelectInput, {
                    [styles.invalidSelectInput]: formData.reason.uuid === "",
                    [styles.inputOpen]: isOpen,
                  })}
                >
                  <p>
                    {formData.reason.name === ""
                      ? t("unavailability.creation.reasonPlaceholder")
                      : t(
                          `unavailabilityReasons.${formData.reason.name}`,
                          formData.reason.name,
                        )}
                  </p>
                  <ArrowIcon
                    className={classNames(styles.arrowIcon, {
                      [styles.arrowIconOpen]: isOpen,
                      [styles.arrowIconClose]: !isOpen,
                    })}
                  />
                </div>
              ),
              list: (
                <div className={styles.list}>
                  {reasons.map((reason) => (
                    <div
                      key={reason.uuid}
                      className={classNames(styles.listItem, {
                        [styles.selectedItem]:
                          reason.uuid === formData.reason.uuid,
                      })}
                      onClick={() => editType(select(reason))}
                    >
                      <p className={styles.itemName}>
                        {t(`unavailabilityReasons.${reason.name}`, reason.name)}
                      </p>
                    </div>
                  ))}
                </div>
              ),
            })}
          </SelectList>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.checkbox} onClick={() => editMode(false)}>
          <Checkbox isChecked={!formData.partialUnavailability} />
          <p className={styles.label}>{t("unavailability.creation.fullDay")}</p>
        </div>
        <div className={styles.checkbox} onClick={() => editMode(true)}>
          <Checkbox isChecked={formData.partialUnavailability} />
          <p className={styles.label}>
            {t("unavailability.creation.partialDay")}
          </p>
        </div>
      </div>
      {!formData.partialUnavailability ? (
        <div className={styles.dateGroup}>
          <div className={styles.row}>
            <h3 className={styles.title}>
              {t("unavailability.creation.startDateTitle")}
            </h3>
            <div className={styles.dateTimeInput}>
              <DateTimeInput
                calendarPosition="TOP"
                dateTime={formData.startDate}
                placeholder={t("unavailability.creation.startDatePlaceholder")}
                input={(startDate) =>
                  editForm({
                    ...formData,
                    startDate,
                    endDate:
                      formData.endDate && formData.endDate !== ""
                        ? formData.endDate < startDate
                          ? startDate
                          : formData.endDate
                        : startDate,
                  })
                }
                isInvalid={
                  error.invalidStartDate ||
                  error.invalidDateRange ||
                  error.invalidUnavailabilityDates
                }
                clear={() => editForm({ ...formData, startDate: "" })}
              />
            </div>
          </div>
          <div className={styles.row}>
            <h3 className={styles.title}>
              {t("unavailability.creation.endDateTitle")}
            </h3>
            <div className={styles.dateTimeInput}>
              <DateTimeInput
                calendarPosition="TOP"
                dateTime={formData.endDate || ""}
                placeholder={t("unavailability.creation.endDatePlaceholder")}
                input={(endDate) => editForm({ ...formData, endDate })}
                clear={() => editForm({ ...formData, endDate: "" })}
                isInvalid={
                  error.invalidDateRange || error.invalidUnavailabilityDates
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.row}>
          <div className={styles.hoursGroup}>
            <h3 className={styles.title}>
              {t("unavailability.creation.durationDay")}
            </h3>
            <DateTimeInput
              calendarPosition="TOP"
              dateTime={formData.startDate || ""}
              placeholder={t("unavailability.creation.startDatePlaceholder")}
              input={(date) => {
                const pickedDay = DateTime.fromISO(date, {
                  zone: "utc",
                }).setZone(userTimezone);
                if (!pickedDay.isValid) return;

                const currentStart = formData.startDate
                  ? DateTime.fromISO(formData.startDate, {
                      zone: "utc",
                    }).setZone(userTimezone)
                  : null;
                const currentEnd = formData.endDate
                  ? DateTime.fromISO(formData.endDate, {
                      zone: "utc",
                    }).setZone(userTimezone)
                  : null;

                const startH = currentStart?.isValid ? currentStart.hour : 0;
                const startM = currentStart?.isValid ? currentStart.minute : 0;
                const endH = currentEnd?.isValid ? currentEnd.hour : 0;
                const endM = currentEnd?.isValid ? currentEnd.minute : 0;

                const newStartDt = DateTime.fromObject(
                  {
                    year: pickedDay.year,
                    month: pickedDay.month,
                    day: pickedDay.day,
                    hour: startH,
                    minute: startM,
                    second: 0,
                    millisecond: 0,
                  },
                  { zone: userTimezone },
                );
                const newEndDt = formData.endDate
                  ? DateTime.fromObject(
                      {
                        year: pickedDay.year,
                        month: pickedDay.month,
                        day: pickedDay.day,
                        hour: endH,
                        minute: endM,
                        second: 0,
                        millisecond: 0,
                      },
                      { zone: userTimezone },
                    )
                  : null;

                if (!newStartDt.isValid) return;

                const newStart = newStartDt.toUTC().toISO() ?? "";
                const newEnd =
                  newEndDt && newEndDt.isValid
                    ? (newEndDt.toUTC().toISO() ?? "")
                    : "";

                editForm({
                  ...formData,
                  startDate: newStart,
                  endDate: newEnd,
                  nonWorkedHoursCount: calculateHours(newStart, newEnd),
                });
              }}
              clear={() =>
                editForm({ ...formData, startDate: "", endDate: "" })
              }
              isInvalid={
                error.invalidStartDate ||
                error.invalidDateRange ||
                error.invalidUnavailabilityDates
              }
            />
          </div>
          <div className={styles.hoursGroup}>
            <h3 className={styles.title}>
              {t("unavailability.creation.durationHour")}
            </h3>
            <TimeInput
              position="top"
              value={formData.startDate}
              input={(newStart) => {
                const newStartDt = DateTime.fromISO(newStart, { zone: "utc" });
                const endDt = formData.endDate
                  ? DateTime.fromISO(formData.endDate, { zone: "utc" })
                  : null;
                const correctedEnd = (
                  endDt
                    ? endDt.isValid && newStartDt.isValid && newStartDt >= endDt
                    : endDt === null
                )
                  ? (newStartDt.plus({ hours: 1 }).toISO() ?? "")
                  : formData.endDate;
                editForm({
                  ...formData,
                  startDate: newStart,
                  endDate: correctedEnd,
                  nonWorkedHoursCount: calculateHours(
                    newStart,
                    correctedEnd || "",
                  ),
                });
              }}
            />
          </div>
          <div className={styles.hoursGroup}>
            <h3 className={styles.title}>
              {t("unavailability.creation.partialEndTime")}
            </h3>
            <TimeInput
              position="top"
              value={formData.endDate || ""}
              input={(newEnd) => {
                const newEndDt = DateTime.fromISO(newEnd, { zone: "utc" });
                const startDt = formData.startDate
                  ? DateTime.fromISO(formData.startDate, { zone: "utc" })
                  : null;
                const corrected =
                  startDt &&
                  startDt.isValid &&
                  newEndDt.isValid &&
                  newEndDt <= startDt
                    ? (startDt.set({ hour: newEndDt.hour }).toISO() ??
                      startDt.plus({ hours: 1 }).toISO() ??
                      "")
                    : newEnd;
                editForm({
                  ...formData,
                  endDate: corrected,
                  nonWorkedHoursCount: calculateHours(
                    formData.startDate,
                    corrected,
                  ),
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { UnavailabilityEditionForm };
